import { useMemo } from "react";
import { useTranslations } from "use-intl";
import { useEligibleModelsAcrossProviders } from "@/features/llm/hooks/use-eligible-models-across-providers";
import { useWorkflowMCPTools } from "@/hooks/api/queries/workflows.queries";

interface Option {
  value: string;
  label: string;
}

export function useAgenticModelOptions() {
  const { models, isLoading } = useEligibleModelsAcrossProviders("chat");

  const options = useMemo<Option[]>(
    () =>
      models.map((item) => ({
        value: item.id,
        label: `${item.providerName} · ${item.model.displayName || item.modelId}`,
      })),
    [models]
  );

  const labelById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.value, option.label])),
    [options]
  );

  return { options, labelById, isLoading };
}

export function useMCPToolOptions() {
  const toolsQuery = useWorkflowMCPTools();
  const tools = toolsQuery.data?.data ?? [];

  const options = useMemo<Option[]>(
    () => tools.map((tool) => ({ value: tool.id, label: tool.name })),
    [tools]
  );

  const labelById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.value, option.label])),
    [options]
  );

  return { options, labelById, isLoading: toolsQuery.isLoading };
}

export function useReferenceOptionsWithCurrent(
  options: Option[],
  currentValue: string | undefined
) {
  const t = useTranslations("workflows");

  return useMemo(() => {
    if (!currentValue || options.some((option) => option.value === currentValue)) {
      return options;
    }
    return [
      ...options,
      {
        value: currentValue,
        label: t("drawer.placeholder.unknown_reference"),
      },
    ];
  }, [currentValue, options, t]);
}
